import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Users, Newspaper, ArrowRight, Info } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { apiClient } from "../lib/api";

export default function TeamPage() {
  const [settings, setSettings] = useState({ about_dpi_governance: "" });

  useEffect(() => {
    apiClient.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="team-page" className="flex flex-1 flex-col gap-8 md:gap-10">
      <section className="space-y-3 md:space-y-4">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4a2d73]">About</p>
        <h1 className="text-balance text-2xl font-semibold tracking-tight text-[#361a54] md:text-3xl">
          About DPI Governance Applied Learning.
        </h1>
        <p className="max-w-prose text-sm text-muted-foreground md:text-base">
          A small team working across Co-Develop and the UCL Institute for Innovation and Public Purpose to build applied
          learning resources on Digital Public Infrastructure governance.
        </p>
      </section>

      <Card className="card-elevated border border-[#d4c5e6] !bg-[#efe6f8]" data-testid="about-dpi-governance-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
            <Info className="h-4 w-4 text-[#1a8bb3]" />
            What is DPI Governance — and why we do this
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700 md:text-base" data-testid="about-dpi-governance-text">
            {settings.about_dpi_governance || "DPI Governance is the set of principles, institutions, policies and practices that ensure Digital Public Infrastructure delivers public value safely and inclusively."}
          </p>
        </CardContent>
      </Card>

      <section className="grid gap-6 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)] md:items-start">
        <Card className="card-elevated">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
              <Users className="h-4 w-4 text-[#1a8bb3]" />
              About the team
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm leading-relaxed text-slate-700">
            <p>
              The DPI Governance Applied Learning team brings together practitioners and researchers who work on the design and
              governance of Digital Public Infrastructure. It is part of the wider Digital Public Infrastructure and digital
              transformation work at the UCL Institute for Innovation and Public Purpose (IIPP), developed in collaboration with
              Co-Develop.
            </p>
            <p>
              This site will gradually host self-paced courses, case studies and executive education offers.
            </p>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div className="rounded-xl border border-[#e8e0f0] bg-white/80 px-3 py-3">
                <p className="text-sm font-semibold text-[#361a54]">David Eaves</p>
                <p className="text-sm text-muted-foreground">Associate Professor in Digital Government</p>
                <p className="text-sm text-slate-700">IIPP UCL</p>
                <a
                  href="https://www.ucl.ac.uk/bartlett/public-purpose/david-eaves"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block text-xs font-medium text-[#1a8bb3] hover:text-[#137a9e]"
                >
                  View profile
                </a>
              </div>
              <div className="rounded-xl border border-[#e8e0f0] bg-white/80 px-3 py-3" data-testid="team-member-naeha-rashid">
                <p className="text-sm font-semibold text-[#361a54]">Naeha Rashid</p>
                <p className="text-sm text-muted-foreground">Project Manager</p>
                <p className="text-sm text-slate-700">IIPP UCL</p>
                <a
                  href="mailto:naeha.rashid@ucl.ac.uk"
                  className="mt-1 inline-block text-xs font-medium text-[#1a8bb3] hover:text-[#137a9e]"
                >
                  naeha.rashid@ucl.ac.uk
                </a>
                <a
                  href="https://www.linkedin.com/in/naeha-rashid-b0728663/"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 ml-2 inline-block text-xs font-medium text-[#1a8bb3] hover:text-[#137a9e]"
                >
                  View profile
                </a>
              </div>
              <div className="rounded-xl border border-[#e8e0f0] bg-white/80 px-3 py-3">
                <p className="text-sm font-semibold text-[#361a54]">Matthew McNaughton</p>
                <p className="text-sm text-muted-foreground">Director, Safety and Inclusion</p>
                <p className="text-sm text-slate-700">Co-Develop</p>
                <a
                  href="https://www.codevelop.fund/matthew-mcnaughton"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block text-xs font-medium text-[#1a8bb3] hover:text-[#137a9e]"
                >
                  View profile
                </a>
              </div>
              <div className="rounded-xl border border-[#e8e0f0] bg-white/80 px-3 py-3">
                <p className="text-sm font-semibold text-[#361a54]">Himansu Pandey</p>
                <p className="text-sm text-muted-foreground">Researcher</p>
                <p className="text-sm text-slate-700">IIPP UCL</p>
                <a
                  href="mailto:Himansu.pandey.24@ucl.ac.uk"
                  className="mt-1 inline-block text-xs font-medium text-[#1a8bb3] hover:text-[#137a9e]"
                  data-testid="himansu-email-link"
                >
                  Himansu.pandey.24@ucl.ac.uk
                </a>
                <a
                  href="https://www.linkedin.com/in/himansupandey/"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 ml-2 inline-block text-xs font-medium text-[#1a8bb3] hover:text-[#137a9e]"
                >
                  View profile
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="card-elevated">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
                <Mail className="h-4 w-4 text-[#1a8bb3]" />
                Contact us
              </CardTitle>
              <CardDescription className="text-sm">
                Get in touch about courses, case studies or potential collaborations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm leading-relaxed text-slate-700">
              <p>
                For questions about the DPI Governance Applied Learning programme, including learning material, potential case
                studies or in-person offerings, please contact the team using the email below.
              </p>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Button
                  asChild
                  className="bg-[#361a54] text-white hover:bg-[#2a1443]"
                  size="sm"
                  data-testid="email-team-btn"
                >
                  <a href="mailto:info@dpigovernance.org?subject=DPI%20Governance%20Applied%20Learning%20enquiry">
                    Email the team
                  </a>
                </Button>
                <p className="text-muted-foreground">info@dpigovernance.org</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="blog-card-link">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-[#361a54]">
                <Newspaper className="h-4 w-4 text-[#1a8bb3]" />
                Blog
              </CardTitle>
              <CardDescription className="text-sm">
                Read more about our work and the journey of developing DPI governance e-learning courses.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-3 border-t border-[#f5f0fa] pt-3 text-xs text-muted-foreground">
              <p>Latest from the team.</p>
              <Button asChild size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]">
                <Link to="/blog" data-testid="open-blog-btn">
                  Read the blog
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="funders-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-[#361a54]">Funders</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-4 text-sm text-slate-700">
              <img
                src="https://customer-assets.emergentagent.com/job_case-study-hub-21/artifacts/mltbfq3e_IIPP%20logo.jpg"
                alt="IIPP logo"
                className="h-12 w-auto rounded bg-white px-1 py-1 shadow-sm object-contain"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_case-study-hub-21/artifacts/7c5oiwwg_ucl%20logo.png"
                alt="UCL logo"
                className="h-12 w-auto rounded bg-white px-3 py-2 shadow-sm object-contain"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_case-study-hub-21/artifacts/o1mdtkgj_Screenshot_2023-09-05_at_19.09.43-removebg-preview.png.webp"
                alt="Co-Develop logo"
                className="h-12 w-auto rounded bg-white px-1 py-1 shadow-sm object-contain"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_case-study-hub-21/artifacts/z9nyjo1c_BMGF%20Logo.png"
                alt="Bill & Melinda Gates Foundation logo"
                className="h-12 w-auto rounded bg-white px-3 py-2 shadow-sm object-contain"
              />
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
